/**
 * Public developer documentation site (docs.vexgate.com).
 *
 * Renders the same DocumentationContent as the merchant portal's /documentation
 * route, but unauthenticated and without the merchant chrome. No profileId is
 * passed, so the examples show the `pro_your_profile_id` placeholder.
 *
 * Deliberately does NOT use AppShell: that pulls in the react-query client,
 * permission-error handling and the auth-oriented error suppressor, none of
 * which a static docs site needs. Only the pieces the content actually depends
 * on are wired here — a Router (for the nuqs react-router adapter), and sonner's
 * Toaster, which the copy-to-clipboard buttons in DocumentationPrimitives use.
 */
/* global __DOCS_UPDATED_ISO__ -- build-time constant, see vite.config.docs.js */
import { BrowserRouter as Router } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { Toaster as Sonner } from 'sonner';

import { DocumentationContent } from '@/shared/pages/Documentation/DocumentationPage';
import logo from '@/assets/logo.png';

function DocsHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1500px] items-center justify-between px-4 sm:px-6">
        <a href="https://vexgate.com" className="flex items-center gap-2.5">
          <img src={logo} alt="" className="h-7 w-auto object-contain" />
          <span className="text-lg font-bold tracking-tight text-slate-950">VEXGATE</span>
        </a>
        <span className="text-sm text-slate-500">Developer documentation</span>
      </div>
    </header>
  );
}

function DocsFooter() {
  // Formatted at a fixed UTC so every reader sees the same day regardless of their
  // timezone. __DOCS_UPDATED_ISO__ is the last commit that touched the documentation
  // content — not the build time — and is baked in by vite.config.docs.js.
  const updated = new Date(__DOCS_UPDATED_ISO__).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <footer className="mt-12 border-t border-slate-200 py-8">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 text-sm text-slate-500 sm:px-6">
        <span>© {new Date().getFullYear()} VEXGATE. All rights reserved.</span>
        <span>
          Last updated <time dateTime={__DOCS_UPDATED_ISO__}>{updated}</time>
        </span>
      </div>
    </footer>
  );
}

export default function DocsApp() {
  return (
    <Router>
      <NuqsAdapter>
        <div className="min-h-screen bg-slate-50">
          <DocsHeader />
          <main className="px-4 py-8 sm:px-6">
            <DocumentationContent />
          </main>
          <DocsFooter />
        </div>
        <Sonner position="top-center" richColors closeButton theme="light" />
      </NuqsAdapter>
    </Router>
  );
}
